/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

type ThemePreference = 'light' | 'dark' | 'system';
type ColorScheme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'theme';

interface ThemeContextValue {
  colorScheme: ColorScheme;
  isDarkMode: boolean;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  colorScheme: 'light',
  isDarkMode: false,
  preference: 'system',
  setPreference: () => {},
  toggleTheme: () => {},
});

function getStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // ignore storage access errors
  }
  return 'system';
}

function getSystemScheme(): ColorScheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreference] = useState<ThemePreference>(() => getStoredPreference());
  const [systemScheme, setSystemScheme] = useState<ColorScheme>(() => getSystemScheme());

  const colorScheme: ColorScheme = useMemo(
    () => (preference === 'system' ? systemScheme : preference),
    [preference, systemScheme],
  );

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      // ignore storage access errors
    }
  }, [preference]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemScheme(event.matches ? 'dark' : 'light');
    };
    setSystemScheme(query.matches ? 'dark' : 'light');
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.style.colorScheme = colorScheme;
    root.setAttribute('data-color-scheme', colorScheme);
    body.style.colorScheme = colorScheme;
    body.setAttribute('data-color-scheme', colorScheme);
    root.classList.add('spectrum', 'spectrum--medium');
    root.classList.toggle('spectrum--light', colorScheme === 'light');
    root.classList.toggle('spectrum--dark', colorScheme === 'dark');
  }, [colorScheme]);

  // Flip between explicit light/dark. First toggle resolves 'system' to current scheme then flips.
  const toggleTheme = () => {
    setPreference((prev) => {
      const current = prev === 'system' ? systemScheme : prev;
      return current === 'dark' ? 'light' : 'dark';
    });
  };

  const value: ThemeContextValue = {
    colorScheme,
    isDarkMode: colorScheme === 'dark',
    preference,
    setPreference,
    toggleTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
